import { useApolloClient } from '@apollo/client'
import { useCallback } from 'react'
import { gql } from '@/lib/graphql/gql'
import type {
  ObtainPasswordTokenQuery,
  ObtainPasswordTokenQueryVariables,
} from '@/lib/hooks/use-password-token.types.gen'

const PASSWORD_TOKEN_QUERY = gql`
  query ObtainPasswordToken($password: String!) {
    passwordToken(password: $password)
  }
`

const INVALID_PASSWORD_MESSAGE = 'Password is invalid'

export type PasswordTokenResult =
  | { passwordToken: string; error?: undefined }
  | { passwordToken?: undefined; error: string }

/**
 * Exchanges the account password for a short-lived password token, which the
 * monolith requires on sensitive mutations such as company invites, company
 * role changes, user removal and two-factor changes.
 */
export const usePasswordToken = () => {
  const client = useApolloClient()

  const obtainPasswordToken = useCallback(
    async (password: string): Promise<PasswordTokenResult> => {
      try {
        const { data } = await client.query<
          ObtainPasswordTokenQuery,
          ObtainPasswordTokenQueryVariables
        >({
          query: PASSWORD_TOKEN_QUERY,
          variables: { password },
          fetchPolicy: 'no-cache',
        })

        if (!data?.passwordToken) {
          return { error: INVALID_PASSWORD_MESSAGE }
        }

        return { passwordToken: data.passwordToken }
      } catch (error) {
        return {
          error:
            error instanceof Error && error.message
              ? error.message
              : INVALID_PASSWORD_MESSAGE,
        }
      }
    },
    [client]
  )

  return { obtainPasswordToken }
}
